import { useState, useEffect, useContext } from 'react';
import { Home, ClipboardList, ClipboardCheck, MessageCircle, Receipt, User, Eye, X } from 'lucide-react';
import HomeScreen from './screens/HomeScreen';
import LocksScreen from './screens/LocksScreen';
import ReceiptsScreen from './screens/ReceiptsScreen';
import InspectScreen from './screens/InspectScreen';
import ConsultScreen from './screens/ConsultScreen';
import TeamMapScreen from './screens/TeamMapScreen';
import OpenWOsScreen from './screens/OpenWOsScreen';
import InsuranceEstimatorScreen from './screens/InsuranceEstimatorScreen';
import ProfileScreen from './screens/ProfileScreen';
import { colors, TopNav, LoadingSpinner } from './shared';
import { usePrompts } from '../contexts/PromptContext';
import { initOpenAI } from '../utils/openai';
import { useAuth } from '../contexts/AuthContext';
import DataContext, { useData } from '../contexts/DataContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { getDefaultTab, getEnabledTabs } from '../utils/featureFlags';

// ─── Tab Definitions (data-driven) ──────────────────────────────────────────

const TAB_DEFINITIONS = [
  { key: 'home',     label: 'Home',     icon: Home },
  { key: 'locks',    label: 'Unit Details', icon: ClipboardList },
  { key: 'inspect',  label: 'Inspect',  icon: ClipboardCheck },
  { key: 'receipts', label: 'Receipts', icon: Receipt },
  { key: 'consult',  label: 'Consult',  icon: MessageCircle },
  { key: 'profile',  label: 'Profile',  icon: User },
];

// POV Banner - shows when admin is viewing as another tech
const PovBanner = () => {
  const { povTechId, setPovTech, getCurrentTech } = useData();
  const currentTech = getCurrentTech();

  if (!povTechId) return null;

  return (
    <div
      className="flex items-center justify-between px-4 py-2"
      style={{ background: colors.orange, color: colors.black }}
    >
      <div className="flex items-center gap-2">
        <Eye size={16} />
        <span className="text-sm font-medium">
          Viewing as: <strong>{currentTech?.name || povTechId}</strong>
        </span>
      </div>
      <button
        onClick={() => setPovTech(null)}
        className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
        style={{ background: 'rgba(0,0,0,0.2)' }}
      >
        <X size={12} />
        Exit
      </button>
    </div>
  );
};

const NavItem = ({ icon: Icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center py-2 px-2 transition-all"
    style={{ color: active ? colors.yellow : colors.textMuted }}
  >
    <Icon size={20} strokeWidth={active ? 2.5 : 1.5} />
    <span className="text-xs mt-1 font-medium">{label}</span>
  </button>
);

const BottomNav = ({ tabs, active, setActive, fixed }) => (
  <div
    className={`${fixed ? 'fixed' : 'absolute'} bottom-0 left-0 right-0 z-40 border-t flex justify-around py-1 px-1 pb-[env(safe-area-inset-bottom)]`}
    style={{ background: colors.black, borderColor: colors.border }}
  >
    {tabs.map(tab => (
      <NavItem
        key={tab.key}
        icon={tab.icon}
        label={tab.label}
        active={active === tab.key}
        onClick={() => setActive(tab.key)}
      />
    ))}
  </div>
);

export default function TradesBeam() {
  // Initialize OpenAI with PromptContext
  const promptContext = usePrompts();
  useEffect(() => {
    initOpenAI(promptContext);
  }, [promptContext]);

  // Safe POV check — useContext returns null when no DataProvider (e.g. insurance estimator route)
  const dataContext = useContext(DataContext);
  const povTechId = dataContext?.povTechId;

  const { flags, loading: flagsLoading } = useFeatureFlags({ simulateTechView: !!povTechId });

  // Check URL for direct route access (e.g., /insurance-estimator, /trades-app)
  const getInitialRoute = () => {
    const path = window.location.pathname;
    if (path.includes('insurance-estimator')) return 'insurance-estimator';
    return getDefaultTab(flags);
  };

  const [active, setActive] = useState(getInitialRoute);
  const [initialRouteSet, setInitialRouteSet] = useState(false);

  // Once flags finish loading, set the correct initial tab (if not a special route)
  useEffect(() => {
    if (!flagsLoading && !initialRouteSet) {
      const path = window.location.pathname;
      if (!path.includes('insurance-estimator')) {
        const defaultTab = getDefaultTab(flags);
        setActive(prev => {
          // Only redirect if current tab is disabled
          if (!flags[prev]) return defaultTab;
          return prev;
        });
      }
      setInitialRouteSet(true);
    }
  }, [flagsLoading, flags, initialRouteSet]);

  // Redirect if current tab gets disabled (real-time flag change)
  useEffect(() => {
    if (initialRouteSet && !flagsLoading && active !== 'insurance-estimator'
        && active !== 'map' && active !== 'open-wos') {
      if (!flags[active]) {
        setActive(getDefaultTab(flags));
      }
    }
  }, [flags, active, flagsLoading, initialRouteSet]);

  // Update URL when route changes (for insurance-estimator)
  useEffect(() => {
    if (active === 'insurance-estimator') {
      window.history.pushState({}, '', '/insurance-estimator');
    }
  }, [active]);

  const enabledTabs = getEnabledTabs(TAB_DEFINITIONS, flags);

  const renderScreen = () => {
    switch (active) {
      case 'home':
        return <HomeScreen setActive={setActive} />;
      case 'locks':
        return <LocksScreen />;
      case 'receipts':
        return <ReceiptsScreen />;
      case 'inspect':
        return <InspectScreen />;
      case 'consult':
        return <ConsultScreen />;
      case 'map':
        return <TeamMapScreen />;
      case 'open-wos':
        return <OpenWOsScreen onBack={() => setActive('home')} />;
      case 'insurance-estimator':
        return <InsuranceEstimatorScreen setActive={setActive} />;
      case 'profile':
        return <ProfileScreen onBack={() => setActive('home')} />;
      default:
        return <HomeScreen setActive={setActive} />;
    }
  };

  // Get current path for nav highlighting
  const currentPath = window.location.pathname;

  // Brief loading state while flags resolve
  if (flagsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.black }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Insurance estimator renders in full-width mode
  if (active === 'insurance-estimator') {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: colors.black }}>
        <TopNav currentPath={currentPath} />
        <PovBanner />
        <div className="flex-1 max-w-4xl mx-auto w-full">
          <InsuranceEstimatorScreen setActive={setActive} />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile: full-screen native layout */}
      <div className="md:hidden h-[100dvh] flex flex-col" style={{ background: colors.black }}>
        <PovBanner />
        <div className="flex-1 overflow-y-auto">
          {renderScreen()}
        </div>
        <BottomNav tabs={enabledTabs} active={active} setActive={setActive} fixed />
      </div>

      {/* Desktop: phone emulator frame */}
      <div className="hidden md:flex min-h-screen flex-col" style={{ background: '#000' }}>
        <TopNav currentPath={currentPath} />
        <PovBanner />
        <div className="flex-1 flex items-center justify-center p-4">
          <div
            className="relative w-full overflow-hidden rounded-3xl shadow-2xl"
            style={{
              maxWidth: 430,
              height: '85vh',
              maxHeight: 800,
              background: colors.black,
              border: `1px solid ${colors.border}`
            }}
          >
            {renderScreen()}
            <BottomNav tabs={enabledTabs} active={active} setActive={setActive} />
          </div>
        </div>
      </div>
    </>
  );
}
